import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import {
    GENERATE_VIDEO,
    POLL_GENERATED_VIDEOS,
    GENERATED_VIDEOS,
    DELETE_GENERATED_VIDEOS,
} from '@/graphql/ads/generated-image-queries'
import { showNotification } from '@/utils/notifications'
import { useWizard } from '@/components/mediaBuying/adCreatives/AdCreativeWizard'

const REPLACEMENT_FAILED = 'The refined video could not be generated, so the original was kept.'

const notifyError = (message) => showNotification(message, 'error', 5000, null, null, false, true)

const notifyFailedGenerations = (count) => {
    notifyError(
        count === 1
            ? 'A video failed to generate and was removed. Please try again.'
            : `${count} videos failed to generate and were removed. Please try again.`,
    )
}

const swapId = (ids, from, to) => ids.map((id) => (id === from ? to : id))

/**
 * Text-to-video generation state: the user's generated videos, polling of the ones still processing,
 * and deletion. Generations that fail are dropped from the list as soon as polling reports them.
 * @param {Object} options
 * @param {(ids: string[]) => void} [options.onVideosDeleted] - Called with the ids the user deleted
 */
export const useVideoGeneration = ({ onVideosDeleted } = {}) => {
    const [selectedFormats, setSelectedFormats] = useState({
        square: true,
        vertical: true,
        horizontal: true,
    })
    const [prompt, setPrompt] = useState('')
    const [isGenerating, setIsGenerating] = useState(false)
    const [currentlyGeneratingVideoIds, setCurrentlyGeneratingVideoIds] = useState([])
    const [generatedVideos, setGeneratedVideos] = useState([])
    const [selectedVideos, setSelectedVideos] = useState([])
    const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false)
    const [idsToDelete, setIdsToDelete] = useState([])
    const [generationProgress, setGenerationProgress] = useState({
        current: 0,
        total: 0,
        percentage: 0,
        startTime: null,
    })
    // The source each refined video still generating replaces, by the refined video's id
    const replacementsRef = useRef({})
    // The latest settleReplacements, so the polling effect need not depend on it
    const settleRef = useRef(() => [])

    const { campaignId } = useWizard()
    const [generateVideo, { loading }] = useMutation(GENERATE_VIDEO)
    const [deleteVideo] = useMutation(DELETE_GENERATED_VIDEOS, {
        onError: () =>
            showNotification(
                'The video could not be deleted. Please try again.',
                'error',
                5000,
                null,
                null,
                false,
                true,
            ),
    })

    const {
        data: pollingData,
        startPolling,
        stopPolling,
    } = useQuery(POLL_GENERATED_VIDEOS, {
        variables: { ids: currentlyGeneratingVideoIds },
        skip: currentlyGeneratingVideoIds.length === 0,
        pollInterval: 5000, // Poll every 5s — Veo takes 1-3 minutes
        fetchPolicy: 'network-only',
    })

    const { refetch: refetchVideos } = useQuery(GENERATED_VIDEOS, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            const videos = data.generatedVideos.filter((v) => v.status !== 'failed')
            setGeneratedVideos(videos)

            const processingVideos = videos.filter((v) => v.status === 'processing')
            if (processingVideos.length > 0) {
                setIsGenerating(true)
                startPolling(5000)
                setCurrentlyGeneratingVideoIds(processingVideos.map((v) => v.id))
                setGenerationProgress({
                    current: 0,
                    total: processingVideos.length,
                    percentage: 0,
                    startTime: Date.now(),
                })
            }
        },
    })

    useEffect(() => {
        const videos = pollingData?.generatedVideosByIds
        const allProcessed = videos && !videos.some((v) => v.status === 'processing')

        if (videos) {
            const restored = settleRef.current(videos)
            const failedIds = videos.filter((v) => v.status === 'failed' && !restored.includes(v.id)).map((v) => v.id)

            setGeneratedVideos((prev) =>
                prev
                    .filter((v) => !failedIds.includes(v.id))
                    .map((v) => {
                        const updated = videos.find((u) => u.id === v.id)
                        return updated ? { ...v, ...updated } : v
                    }),
            )

            if (failedIds.length > 0) {
                notifyFailedGenerations(failedIds.length)
                if (!allProcessed) {
                    setCurrentlyGeneratingVideoIds((prev) => prev.filter((id) => !failedIds.includes(id)))
                }
            }

            const completedCount = videos.filter((v) => v.status !== 'processing').length
            setGenerationProgress((prev) => ({
                ...prev,
                current: completedCount,
                total: prev.total === 0 ? videos.length : prev.total,
            }))
        }

        if (allProcessed) {
            setCurrentlyGeneratingVideoIds([])
            stopPolling()
            setIsGenerating(false)
            setGenerationProgress((prev) => ({ ...prev, percentage: 100 }))
            setTimeout(() => {
                setGenerationProgress({ current: 0, total: 0, percentage: 0, startTime: null })
            }, 1000)
        }
    }, [pollingData, stopPolling])

    // Progress simulation — one 15s Veo generation (clip + extension, ~2-6 minutes) serves every format,
    // since square and vertical are cropped from the horizontal video
    useEffect(() => {
        if (!isGenerating || generationProgress.total === 0) return

        const interval = setInterval(() => {
            setGenerationProgress((prev) => {
                if (!prev.startTime) return prev

                const elapsed = Math.floor((Date.now() - prev.startTime) / 1000)
                const totalEstimatedTime = 240 // 4 minutes estimated
                const actualPercentage = (prev.current / prev.total) * 100

                let simulated
                if (elapsed <= totalEstimatedTime) {
                    simulated = (elapsed / totalEstimatedTime) * 80
                } else {
                    const overtime = elapsed - totalEstimatedTime
                    let extra = 0
                    let inc = 5
                    let t = 0
                    while (t < overtime) {
                        extra += (Math.min(10, overtime - t) / 10) * inc
                        inc = inc / 2
                        t += 10
                        if (extra > 19) {
                            extra = 19
                            break
                        }
                    }
                    simulated = 80 + extra
                }

                return { ...prev, percentage: Math.round(Math.max(simulated, actualPercentage)) }
            })
        }, 1000)

        return () => clearInterval(interval)
    }, [isGenerating, generationProgress.total])

    // Reloads the library
    const refetch = async () => {
        const { data } = await refetchVideos()
        if (data?.generatedVideos) setGeneratedVideos(data.generatedVideos.filter((v) => v.status !== 'failed'))
    }

    // Removes the source a refined video replaced, from the server and from the draft it was added to; the list
    // already shows the refined one in its place
    const removeReplacedSource = (source) => {
        deleteVideo({ variables: { ids: [source.id] } })
        onVideosDeleted?.([source.id])
    }

    // Puts a completed refined video in its source's place (and selection) and removes the source
    const replaceAsset = (source, asset) => {
        setGeneratedVideos((prev) =>
            prev.some((v) => v.id === source.id) ? prev.map((v) => (v.id === source.id ? asset : v)) : [asset, ...prev],
        )
        setSelectedVideos((prev) => swapId(prev, source.id, asset.id))
        removeReplacedSource(source)
    }

    // Finishes the replacements among polled records: a completed one drops its source, a failed one gives the
    // source's place back. Returns the ids of the failed records whose source was restored
    const settleReplacements = (records) => {
        const restored = []
        records.forEach((record) => {
            const source = replacementsRef.current[record.id]
            if (!source || record.status === 'processing') return
            delete replacementsRef.current[record.id]
            if (record.status === 'completed') {
                removeReplacedSource(source)
            } else {
                restored.push(record.id)
                setGeneratedVideos((prev) => prev.map((v) => (v.id === record.id ? source : v)))
                setSelectedVideos((prev) => swapId(prev, record.id, source.id))
                notifyError(REPLACEMENT_FAILED)
            }
        })
        return restored
    }
    settleRef.current = settleReplacements

    // Shows a record another flow started (a refine turn closed mid-generation) and polls it like our own; with
    // `replaces`, the record takes that source's place and the source goes once the record completes
    const trackGenerating = (record, replaces = null) => {
        if (replaces) {
            replacementsRef.current[record.id] = replaces
            setSelectedVideos((prev) => swapId(prev, replaces.id, record.id))
        }
        setGeneratedVideos((prev) => {
            if (prev.some((v) => v.id === record.id)) return prev
            if (replaces && prev.some((v) => v.id === replaces.id)) {
                return prev.map((v) => (v.id === replaces.id ? record : v))
            }
            return [record, ...prev]
        })
        setCurrentlyGeneratingVideoIds((prev) => (prev.includes(record.id) ? prev : [record.id, ...prev]))
        setIsGenerating(true)
        setGenerationProgress((prev) =>
            prev.total > 0
                ? { ...prev, total: prev.total + 1 }
                : { current: 0, total: 1, percentage: 0, startTime: Date.now() },
        )
        setTimeout(() => startPolling(5000), 0)
    }

    const handleFormatSelection = (format) => {
        setSelectedFormats((prev) => ({ ...prev, [format]: !prev[format] }))
    }

    const handleGenerate = async () => {
        const formats = Object.keys(selectedFormats).filter((k) => selectedFormats[k])
        if (formats.length === 0 || !prompt.trim()) return

        setGenerationProgress({
            current: 0,
            total: formats.length,
            percentage: 0,
            startTime: Date.now(),
        })

        // The license's logo is stamped on the outputs and its brand style guides the model
        const result = await generateVideo({
            variables: { input: { prompt, formats, ...(campaignId && { campaignId }) } },
        })
        const newVideos = result.data?.generateVideo || []

        if (newVideos.length) {
            const ids = newVideos.map((v) => v.id)
            setGeneratedVideos((prev) => [...newVideos, ...prev])
            setCurrentlyGeneratingVideoIds((prev) => [...ids, ...prev])
            setIsGenerating(true)
            setTimeout(() => startPolling(5000), 0)
        }
    }

    const handleStopGenerate = () => handleDelete(currentlyGeneratingVideoIds)

    const handleDelete = (ids) => {
        setIdsToDelete(ids)
        setShowDeleteConfirmationModal(true)
    }

    const handleConfirmDelete = () => {
        const stillGenerating = currentlyGeneratingVideoIds.filter((id) => !idsToDelete.includes(id))

        deleteVideo({ variables: { ids: idsToDelete } })
        setGeneratedVideos((prev) => prev.filter((v) => !idsToDelete.includes(v.id)))
        setSelectedVideos((prev) => prev.filter((id) => !idsToDelete.includes(id)))
        setCurrentlyGeneratingVideoIds(stillGenerating)
        setShowDeleteConfirmationModal(false)
        if (stillGenerating.length === 0) setIsGenerating(false)
        setIdsToDelete([])
        onVideosDeleted?.(idsToDelete)
    }

    const handleSelect = (id) => {
        setSelectedVideos((prev) => {
            const isSelected = prev.includes(id)
            return isSelected ? prev.filter((v) => v !== id) : [id] // single selection for video
        })
    }

    return {
        selectedFormats,
        prompt,
        isGenerating,
        generatedVideos,
        selectedVideos,
        showDeleteConfirmationModal,
        loading,
        generationProgress,
        refetch,
        replaceAsset,
        trackGenerating,
        setPrompt,
        handleFormatSelection,
        handleGenerate,
        handleStopGenerate,
        handleDelete,
        handleConfirmDelete,
        handleSelect,
        setShowDeleteConfirmationModal,
        setSelectedVideos,
    }
}
